import Logger from '../util/Logger';

const FAKE_LOGGER = {
  log (...args) {
    Logger.info(...args);
  }
};

/**
 * Need to do this since import happens before window exists
 * @return {*}
 */
const getLogger = function getLogger () {
  return FAKE_LOGGER;
};

export const clean = function clean (content, event) {
  content.user_created = false;
  content.can_edit = false;
  if (event) {
    // If the user is the creator, we assume server sets this flag
    content.can_delete = event.user_created || false;
  } else {
    content.can_delete = false;
  }
  content.user_vote = null;
  if (content.creator_info) {
    content.creator_info.is_current_user = false;
  }
  return content;
};

export const adoptExisting = function adoptExisting (content, existing) {
  getLogger().log('Adopt: ', existing);
  if (content.creator_info && existing.creator_info) {
    content.creator_info.is_current_user = existing.creator_info.is_current_user;
  }
  content.user_created = existing.user_created;
  content.can_edit = existing.can_edit;
  content.can_delete = existing.can_delete;
  content.user_vote = existing.user_vote;
};

/**
 * Do something to a matching element in a content list and return the changed list
 * @param contentList
 * @param isMatching
 * @param callback
 * @return [] new list
 */
const whenMatchingContentIsFound = function whenMatchingContentIsFound (
  contentList,
  isMatching,
  callback
) {
  // Copy out the list of contents
  const copyOfContents = contentList || [];

  for (let i = 0; i < copyOfContents.length; ++i) {
    const existingContent = copyOfContents[i];
    if (isMatching(existingContent)) {
      callback(i, copyOfContents);
      break;
    }
  }

  return copyOfContents;
};

export class RealtimeUi {

  /**
   * Generic top level content was created - add it to the list
   *
   * @param event
   * @param data
   * @param contentList
   * @return [] new contentList
   */
  static onContentCreated (event, data, contentList) {
    // Strip out creator_info because its wrong
    const content = clean(data, event);

    // Trigger new animations
    content.newlyAdded = true;

    // Copy out the list of contents
    const copyOfContents = contentList || [];

    // Make sure we are not adding duplicates
    let contained = -1;
    for (let i = 0; i < copyOfContents.length; ++i) {
      const existingContent = copyOfContents[i];
      if (existingContent.id === content.id) {
        contained = i;
        break;
      }
    }

    // Add the new content to the list
    if (contained < 0) {
      getLogger().log('Content created new', content);
      copyOfContents.push(content);
    } else {
      getLogger().log('Content created updated', content);
      const old = copyOfContents[contained];
      adoptExisting(content, old);
      copyOfContents[contained] = content;
    }

    return copyOfContents;
  }

  /**
   * Generic top level content was edited - change it in the list
   *
   * @param event
   * @param data
   * @param contentList
   * @return [] edited contentList
   */
  static onContentEdited (event, data, contentList) {
    // Trigger new animations
    const content = clean(data, event);
    content.newlyAdded = true;

    return whenMatchingContentIsFound(contentList, existingContent => {
      // If the ids match
      return content.id === existingContent.id;
    }, (i, newContentList) => {
      getLogger().log('Content edited', content);
      // Update the existing entry to the new content
      const old = newContentList[i];
      adoptExisting(content, old);
      newContentList[i] = content;
    });

  }

  /**
   * Generic top level content was deleted - deleted it from the list
   *
   * @param event
   * @param data
   * @param contentList
   * @return [] new contentList
   */
  static onContentDeleted (event, data, contentList) {
    // Trigger deleted animations
    const content = clean(data, event);
    content.uiDeleted = true;

    return whenMatchingContentIsFound(contentList, existingContent => {
      // If the ids match
      return content.id === existingContent.id;
    }, (i, newContentList) => {
      getLogger().log('Content deleted', content);
      // Delete the entry
      newContentList.splice(i, 1);
    });

  }

  /**
   * Generic top level content was stubbed - change it in the list
   *
   * @param event
   * @param data
   * @param contentList
   * @return [] stubbed contentList
   */
  static onContentStubbed (event, data, contentList) {
    // Trigger deleted animations
    const content = clean(data, event);

    // Don't mark as deleted as this hides the card from the list. It's just stubbed.
    // content.uiDeleted = true;

    return whenMatchingContentIsFound(contentList, existingContent => {
      // If the ids match
      return content.id === existingContent.id;
    }, (i, newContentList) => {
      getLogger().log('Content stubbed', content);
      // Update the existing entry to the new content
      const old = newContentList[i];
      adoptExisting(content, old);
      newContentList[i] = content;
    });
  }

  /**
   * On stats updated
   *
   * @param event
   * @param data
   * @param contentList
   * @return {*[]}
   */
  static onContentStatsUpdated (event, data, contentList) {
    const stats = clean(data, event);

    return whenMatchingContentIsFound(contentList, existingContent => {
      // If id match
      return stats.id === existingContent.id;
    }, (i, newContentList) => {
      getLogger().log('Content stats updated', stats);
      // Update the stats on the found item
      // We only pull number stats off this event since we are not guaranteed other content accuracy
      const newContent = newContentList[i];
      newContent.like_count = stats.like_count;
      newContent.dislike_count = stats.dislike_count;
      newContent.comment_count = stats.comment_count;
      newContent.hero_replied = stats.hero_replied;
    });
  }

  /**
   * Generic nested content was created - find it in the content list and add it to the parent
   *
   * @param event
   * @param data
   * @param contentList
   * @param parentId
   * @return [] new contentList
   */
  static onNestedContentCreated (event, data, contentList, parentId) {
    const content = clean(data, event);
    content.newlyAdded = true;

    return whenMatchingContentIsFound(contentList, existingContent => {
      // If the id matches the parentId
      return existingContent.id === parentId;
    }, (i, newContentList) => {
      // Find the list of nested comments on the content and add this new comment to it
      const parent = newContentList[i];

      // Copy out the list of contents
      const copyOfComments = parent.comments || [];

      // Make sure we are not adding duplicates
      let contained = -1;
      for (let i = 0; i < copyOfComments.length; ++i) {
        const exitingComment = copyOfComments[i];
        if (exitingComment.id === content.id) {
          contained = i;
          break;
        }
      }

      // Add the new content to the list
      if (contained < 0) {
        getLogger().log('Nested content created new', content);
        copyOfComments.push(content);
      } else {
        getLogger().log('Nested content created updated', content);
        const old = copyOfComments[i];
        adoptExisting(content, old);
        copyOfComments[contained] = content;
      }

      parent.comments = copyOfComments;
    });
  }

  /**
   * Generic nested content was edited - find it in the content list and change it in the parent
   *
   * @param event
   * @param data
   * @param contentList
   * @param parentId
   * @return [] edited contentList
   */
  static onNestedContentEdited (event, data, contentList, parentId) {
    // Trigger new animations
    const content = clean(data, event);
    content.newlyAdded = true;

    return whenMatchingContentIsFound(contentList, existingContent => {
      // If the id matches the parentId
      return existingContent.id === parentId;
    }, (i, newContentList) => {
      // Find the list of nested comments on the content and change the marked one
      const parent = newContentList[i];
      if (!!parent.comments) {
        parent.comments = whenMatchingContentIsFound(parent.comments, existingNestedComment => {
          // content.id equals nested content.id
          return content.id === existingNestedComment.id;
        }, (j, newNestedContentList) => {
          getLogger().log('Nested content edited', content);
          // Update the nested content to this new stuff
          const old = newNestedContentList[i];
          adoptExisting(content, old);
          newNestedContentList[j] = content;
        });
      }
    });
  }

  /**
   * Generic nested content was deleted - find it in the content list and delete it in the parent
   *
   * @param event
   * @param data
   * @param contentList
   * @param parentId
   * @return [] new contentList
   */
  static onNestedContentDeleted (event, data, contentList, parentId) {
    // Trigger deleted animations
    const content = clean(data, event);
    content.uiDeleted = true;

    return whenMatchingContentIsFound(contentList, existingContent => {
      // If the id matches the parentId
      return existingContent.id === parentId;
    }, (i, newContentList) => {
      // Find the list of nested comments on the content and change the marked one
      const parent = newContentList[i];
      if (!!parent.comments) {
        parent.comments = whenMatchingContentIsFound(parent.comments, existingNestedComment => {
          // content.id equals nested content.id
          return content.id === existingNestedComment.id;
        }, (j, newNestedContentList) => {
          getLogger().log('Nested content deleted', content);
          // Delete the nested comment from the comment list
          newNestedContentList.splice(j, 1);
        });
      }
    });
  }

  /**
   * Generic nested content was stubbed - find it in the content list and stub it in the parent
   *
   * @param event
   * @param data
   * @param contentList
   * @param parentId
   * @return [] stubbed contentList
   */
  static onNestedContentStubbed (event, data, contentList, parentId) {
    // Trigger deleted animations
    const content = clean(data, event);

    // Don't mark as deleted as this hides the card from the list. It's just stubbed.
    // content.uiDeleted = true;

    return whenMatchingContentIsFound(contentList, existingContent => {
      // If the id matches the parentId
      return existingContent.id === parentId;
    }, (i, newContentList) => {
      // Find the list of nested comments on the content and change the marked one
      const parent = newContentList[i];
      if (!!parent.comments) {
        parent.comments = whenMatchingContentIsFound(parent.comments, existingNestedComment => {
          // content.id equals nested content.id
          return content.id === existingNestedComment.id;
        }, (j, newNestedContentList) => {
          getLogger().log('Nested content stubbed', content);
          // Update the nested content to this new stuff
          const old = newNestedContentList[i];
          adoptExisting(content, old);
          newNestedContentList[j] = content;
        });
      }
    });
  }

  /**
   * On nested stat update
   *
   * @param event
   * @param data
   * @param contentList
   * @param parentId
   * @return {*[]}
   */
  static onNestedContentStatsUpdated (event, data, contentList, parentId) {
    const stats = clean(data, event);
    return whenMatchingContentIsFound(contentList, existingContent => {
      // If id match
      return parentId === existingContent.id;
    }, (i, newContentList) => {
      // Update the stats on the found item
      // We only pull number stats off this event since we are not guaranteed other content accuracy
      const parent = newContentList[i];
      if (!!parent.comments) {
        parent.comments = whenMatchingContentIsFound(parent.comments, existingNestedComment => {
          // stats.id equals nested content id
          return stats.id === existingNestedComment.id;
        }, (j, newNestedContentList) => {
          getLogger().log('Nested content stats updated', stats);
          const newContent = newNestedContentList[j];
          newContent.like_count = stats.like_count;
          newContent.dislike_count = stats.dislike_count;
          newContent.comment_count = stats.comment_count;
          newContent.hero_replied = stats.hero_replied;
        });
      }
    });
  }

  static onVoteOccurredOnUserContent (
    stats, contentList, onlyShowCreatorEvents, onLike, onDislike, onComment) {
    const handleCallback = (content, stats, onLike, onDislike, onComment) => {
      const idMatches = content.id === stats.id;
      const isCurrentUserContent = content.creator_info && content.creator_info.is_current_user;
      if (idMatches && (isCurrentUserContent || !onlyShowCreatorEvents)) {
        // If we have found the Id, adopt the existing creator info as it should be correct
        // TODO: This is technically a bug in the realtime bus since it exposes unreliable creator_info
        const isCurrentUserEvent = stats.creator_info && stats.creator_info.is_current_user;

        if (content.like_count < stats.like_count &&
          (!isCurrentUserEvent || !onlyShowCreatorEvents)) {
          // Someone liked your stuff
          onLike(stats);
          return true;
        }

        if (content.dislike_count < stats.dislike_count &&
          (!isCurrentUserEvent || !onlyShowCreatorEvents)) {
          onDislike(stats);
          return true;
        }

        if (content.comment_count < stats.comment_count &&
          (!isCurrentUserEvent || !onlyShowCreatorEvents)) {
          onComment(stats);
          return true;
        }
      }

      return false;
    };

    // Clean out any old creator info
    let handled = false;
    for (let topLevelContent of contentList) {
      if (!handleCallback(topLevelContent, stats, onLike, onDislike, onComment)) {
        if (!!topLevelContent.comments) {
          for (let comment of topLevelContent.comments) {
            handleCallback(comment, stats, onLike, onDislike, onComment);
            handled = true;
          }
        }
      }

      if (handled) {
        break;
      }
    }
  }

}

